import { Link } from 'react-router-dom';
import { Lock, Flame, Stars, CircleDot, Eclipse, Sun } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { useLanguage } from '@/i18n/LanguageContext';
import { useThemeRooms } from '@/hooks/useThemeRooms';

interface ThemeRoom {
  id: string;
  title: string;
  subtitle: string;
  requiredTheme: 'ash' | 'twilight' | 'void' | 'eclipse' | 'dawn';
  path: string;
  icon: React.ReactNode;
  gradient: string;
  borderColor: string;
  textColor: string;
  timeRestriction?: string;
}

const themeRooms: ThemeRoom[] = [
  {
    id: 'ash-embers',
    title: 'Ash & Embers',
    subtitle: 'Where fire once lived',
    requiredTheme: 'ash',
    path: '/room/ash-embers',
    icon: <Flame className="w-5 h-5" />,
    gradient: 'from-orange-500/10 to-red-900/10',
    borderColor: 'border-orange-500/30',
    textColor: 'text-orange-400',
  },
  {
    id: 'twilight-stars',
    title: 'Twilight Stars',
    subtitle: 'Connect the silence',
    requiredTheme: 'twilight',
    path: '/room/twilight-stars',
    icon: <Stars className="w-5 h-5" />,
    gradient: 'from-indigo-500/10 to-purple-900/10',
    borderColor: 'border-indigo-500/30',
    textColor: 'text-indigo-400',
  },
  {
    id: 'void-abyss',
    title: 'Void Abyss',
    subtitle: 'The darkness stares back',
    requiredTheme: 'void',
    path: '/room/void-abyss',
    icon: <CircleDot className="w-5 h-5" />,
    gradient: 'from-gray-500/10 to-black',
    borderColor: 'border-white/10',
    textColor: 'text-white/60',
  },
  {
    id: 'eclipse-shadows',
    title: 'Eclipse Shadows',
    subtitle: 'Speak into the total darkness',
    requiredTheme: 'eclipse',
    path: '/room/eclipse-shadows',
    icon: <Eclipse className="w-5 h-5" />,
    gradient: 'from-amber-500/10 to-black',
    borderColor: 'border-amber-500/20',
    textColor: 'text-amber-400',
  },
  {
    id: 'dawn-awakening',
    title: 'Dawn Awakening',
    subtitle: 'Greet the first light',
    requiredTheme: 'dawn',
    path: '/room/dawn-awakening',
    icon: <Sun className="w-5 h-5" />,
    gradient: 'from-orange-400/10 to-pink-500/10',
    borderColor: 'border-orange-400/30',
    textColor: 'text-orange-300',
    timeRestriction: '5-7 AM',
  },
];

const ThemeExclusiveRoomsSection = () => {
  const { theme } = useTheme();
  const { t, isRTL } = useLanguage();
  const { isDawnAvailable } = useThemeRooms();

  return (
    <section className="px-4 sm:px-6 pb-8">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-xs uppercase tracking-widest text-muted-foreground/40 mb-4 flex items-center gap-2">
          <Lock className="w-3 h-3" /> {t('theme_exclusive_rooms')}
        </h2>
        <p className="text-muted-foreground/30 text-xs mb-4">
          {t('theme_exclusive_hint')}
        </p>
        
        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {themeRooms.map((room) => {
            const isThemeActive = theme === room.requiredTheme;
            const isDawnLocked = room.requiredTheme === 'dawn' && !isDawnAvailable();
            const isUnlocked = isThemeActive && !isDawnLocked;
            
            return (
              <div key={room.id} className="relative group">
                {isUnlocked ? (
                  <Link
                    to={room.path}
                    className={`block p-6 rounded-lg border ${room.borderColor} bg-gradient-to-br ${room.gradient} transition-all hover:scale-[1.02] hover:shadow-lg`}
                  >
                    <div className={`${room.textColor} mb-3 ${isRTL ? 'rtl-flip' : ''}`}>
                      {room.icon}
                    </div>
                    <h3 className={`font-light text-sm ${room.textColor}`}>
                      {t(`room_${room.id}`) !== `room_${room.id}` ? t(`room_${room.id}`) : room.title}
                    </h3>
                    <p className="text-muted-foreground/40 text-xs mt-1">
                      {t(`room_${room.id}_subtitle`) !== `room_${room.id}_subtitle` ? t(`room_${room.id}_subtitle`) : room.subtitle}
                    </p>
                    <span className="absolute top-3 right-3 text-[10px] text-green-400/60">
                      {t('unlocked')}
                    </span>
                  </Link>
                ) : (
                  <div className="p-6 rounded-lg border border-border/20 bg-card/20 opacity-60 cursor-not-allowed">
                    <div className="text-muted-foreground/30 mb-3 flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      {room.icon}
                    </div>
                    <h3 className="font-light text-sm text-muted-foreground/50">
                      {t(`room_${room.id}`) !== `room_${room.id}` ? t(`room_${room.id}`) : room.title}
                    </h3>
                    <p className="text-muted-foreground/30 text-xs mt-1">
                      {t('requires_theme')} <span className={room.textColor}>{t(`theme_${room.requiredTheme}`)}</span>
                      {room.timeRestriction && <span className="block mt-0.5">({room.timeRestriction})</span>}
                    </p>
                    
                    {/* Unlock hint on hover */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-background/80 rounded-lg">
                      <Link 
                        to="/settings" 
                        className="text-xs text-muted-foreground/60 hover:text-foreground transition-colors flex items-center gap-2"
                      >
                        {t('switch_to')} {t(`theme_${room.requiredTheme}`)} →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ThemeExclusiveRoomsSection;
